import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useKanbanApi } from "@/utils/kanban-api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { KanbanObservacao } from "@/types/kanban";
import { MessageSquare, Send, User as UserIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toUserError } from "@/lib/api-error";
// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

interface ObservacoesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  posicaoId: string;
  freelancerNome: string;
}

export function ObservacoesModal({
  open,
  onOpenChange,
  posicaoId,
  freelancerNome,
}: ObservacoesModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const kanbanApi = useKanbanApi();
  const [novaMensagem, setNovaMensagem] = useState("");

  const { data: observacoes, isLoading } = useQuery({
    queryKey: ["kanban-observacoes", posicaoId],
    queryFn: () => kanbanApi.getObservacoes(posicaoId),
    enabled: open && !!posicaoId,
  });

  const addObservacaoMutation = useMutation({
    mutationFn: (mensagem: string) =>
      kanbanApi.addObservacao(posicaoId, mensagem, user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kanban-observacoes", posicaoId],
      });
      // Atualiza o badge de contagem nos cards (batch no nível da vaga). Prefixo
      // sem vagaId: o modal só conhece a posição, e o match por prefixo cobre a
      // entrada da vaga atual.
      queryClient.invalidateQueries({
        queryKey: ["kanban-observacoes-counts"],
      });
      setNovaMensagem("");
      toast({
        title: "Sucesso",
        description: "Observação adicionada com sucesso!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: "Não foi possível adicionar a observação. " + toUserError(error).description,
        variant: "destructive",
      });
      console.error("Erro ao adicionar observação:", error);
    },
  });

  useEffect(() => {
    if (!open) {
      setNovaMensagem("");
    }
  }, [open]);

  const handleEnviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaMensagem.trim()) return;

    addObservacaoMutation.mutate(novaMensagem);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setNovaMensagem("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Observações - {freelancerNome}
          </DialogTitle>
          <DialogDescription>
            Adicione observações sobre este candidato
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center text-slate-500 py-8">
                Carregando observações...
              </div>
            ) : observacoes && observacoes.length > 0 ? (
              observacoes.map((obs: KanbanObservacao) => (
                <div
                  key={obs.id}
                  className={`flex gap-3 ${
                    obs.user_id === user?.id ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage
                      src={obs.usuario?.avatar_url || undefined}
                      alt={obs.usuario?.full_name || obs.usuario?.username}
                    />
                    <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                      {obs.usuario?.full_name
                        ? getInitials(obs.usuario.full_name)
                        : obs.usuario?.username
                        ? getInitials(obs.usuario.username)
                        : "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={`flex flex-col max-w-[80%] ${
                      obs.user_id === user?.id ? "items-end" : "items-start"
                    }`}
                  >
                    <div
                      className={`rounded-lg p-3 ${
                        obs.user_id === user?.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm wrap-break-word whitespace-pre-wrap">
                        {obs.mensagem}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                      <span>
                        {obs.usuario?.full_name ||
                          obs.usuario?.username ||
                          "Usuário desconhecido"}
                      </span>
                      <span>•</span>
                      <span>
                        {format(new Date(obs.created_at), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-slate-500 py-8">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>Nenhuma observação ainda</p>
                <p className="text-xs mt-1">
                  Seja o primeiro a adicionar uma observação
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        <form onSubmit={handleEnviar} className="pt-4 border-t">
          <div className="flex gap-2">
            <Textarea
              value={novaMensagem}
              onChange={(e) => setNovaMensagem(e.target.value)}
              placeholder="Digite sua observação..."
              className="resize-none"
              rows={3}
              disabled={addObservacaoMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (novaMensagem.trim()) {
                    handleEnviar(e);
                  }
                }
              }}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!novaMensagem.trim() || addObservacaoMutation.isPending}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

